/**
 * Channel URL builders for the desktop fallback share drawer.
 *
 * <p>A pure module with no React import, so it is unit-testable in isolation and the drawer component
 * stays thin. Third-party intent endpoints drift; keeping them in one place with one pinned spelling each
 * means the worst case is a channel opening a working page instead of a composer, rather than a bug
 * hunt across components.
 */

export type ShareChannel = "clipboard" | "whatsapp" | "x" | "facebook" | "reddit" | "linkedin";

/**
 * The drawer's channel order, and each one has to earn its place.
 *
 * <ol>
 *   <li><strong>Copy link</strong> — ~75–85% of sharing is copy-paste into private channels ("dark
 *       social"). It is first, it takes initial focus, and it is the ONLY channel offered on restricted
 *       entities. It is also the only one that works for a destination we have never heard of.</li>
 *   <li><strong>WhatsApp</strong> — the beachhead channel, >90% reach across BR/MX/CO/AR/CL/ES. The
 *       2026-05-18 design put WhatsApp in its NON-GOALS and shipped X + LinkedIn instead; that ranking
 *       optimizes for a US professional-network audience we are explicitly not pursuing, and this
 *       reverses it.</li>
 *   <li><strong>X</strong> — kept because es/pt poetry and literary communities are active there, and it
 *       is one of the two public-discovery channels here.</li>
 *   <li><strong>Facebook</strong> — added 2026-07-27, reversing a decline. The original objection was
 *       that `sharer.php` strips custom text; that is the same property LinkedIn has, and LinkedIn ships
 *       precisely BECAUSE it is one parameter plus the OG card we already emit. Facebook remains dominant
 *       across the BR/MX/CO beachhead.</li>
 *   <li><strong>Reddit</strong> — added 2026-07-27, reversing a decline. The standing
 *       paid-Ads-plus-human-participation policy governs SEAVIEN posting to Reddit; a submit link a reader
 *       clicks is not Seavien posting, no more than the clipboard is. It is the other public-discovery
 *       channel, and where long-form expressive writing actually travels in English.</li>
 *   <li><strong>LinkedIn</strong> — ranked last and shipped only because it is nearly free: one
 *       parameter, no text (it ignores custom text and renders our OG card, which we already emit). A
 *       professional network is close to the worst content fit expressive writing has. This is a
 *       demotion, not an endorsement, and it carries a REMOVAL CRITERION: if it is under 2% of drawer
 *       channel clicks 60 days after release, delete the button. An unused channel button is negative
 *       real estate, not neutral. Facebook and Reddit inherit the same criterion.</li>
 * </ol>
 *
 * <p><strong>Six is the ceiling.</strong> The picker renders as a single glanceable row of marks; a
 * seventh channel turns it into a menu, which is the thing the native sheet already does better.
 */
export const RANKED_CHANNELS: readonly ShareChannel[] =
    ["clipboard", "whatsapp", "x", "facebook", "reddit", "linkedin"];

/** Channels offered when the entity is restricted (private, deleted, archived, or visibility unknown). */
export const RESTRICTED_CHANNELS: readonly ShareChannel[] = ["clipboard"];

/**
 * Builds the outbound intent URL for a channel.
 *
 * <p>Returns null for `clipboard`, which is not a navigation.
 *
 * @param url  the share URL, already carrying its `?sv_s` token
 * @param text the composed share text, WITHOUT a URL inside it
 */
export function buildChannelUrl(channel: ShareChannel, url: string, text?: string): string | null {
    const encodedUrl = encodeURIComponent(url);
    switch (channel) {
        case "clipboard":
            return null;
        case "whatsapp":
            // wa.me takes a SINGLE `text` parameter, so here the URL genuinely does go inside the text.
            // This is the one place the no-URL-in-text rule does not apply, precisely because there is no
            // separate `url` field for it to duplicate against.
            return `https://wa.me/?text=${encodeURIComponent(text ? `${text} ${url}` : url)}`;
        case "x":
            // Pinned to x.com/intent/post. twitter.com/intent/tweet still redirects, but two spellings of
            // the same endpoint in one codebase is a future bug rather than a nicety.
            return text
                ? `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodedUrl}`
                : `https://x.com/intent/post?url=${encodedUrl}`;
        case "facebook":
            // Like LinkedIn, Facebook strips any custom text and renders the OG card we already emit, so
            // sending text would be pure noise on the wire. `u` is the only parameter `sharer.php` reads.
            return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        case "reddit":
            // The one channel where `title` is not decorative: it prefills the SUBMISSION title, which is
            // the single most load-bearing field of a Reddit post. So the composed share text goes here
            // even though it is a `title` parameter — and the URL stays in its own field, so the §3.3
            // no-URL-in-text rule applies normally.
            return text
                ? `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(text)}`
                : `https://www.reddit.com/submit?url=${encodedUrl}`;
        case "linkedin":
            // LinkedIn ignores custom text entirely and renders the OG card, so sending text would be
            // pure noise on the wire.
            return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        default:
            return null;
    }
}

/** The GA4 `method` / `details_json.channel` value for a channel. Identical by construction today. */
export function channelAnalyticsValue(channel: ShareChannel): ShareChannel {
    return channel;
}
